import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { trackEvent } from "../trackingUtils";

const Layout = ({ children }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location]);

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/services", label: "Services" },
    { path: "/blog", label: "Blog" },
    { path: "/careers", label: "Careers" },
    { path: "/about", label: "About" },
    { path: "/contact", label: "Contact" },
  ];

  const footerSections = [
    {
      title: "Services",
      links: [
        { label: "AI & Automation", path: "/services/ai" },
        { label: "Cloud Solutions", path: "/services/cloud" },
        { label: "Data Analytics", path: "/services/data" },
        { label: "Software Development", path: "/services/software" },
      ],
    },
    {
      title: "Company",
      links: [
        { label: "About Us", path: "/about" },
        { label: "Careers", path: "/careers" },
        { label: "Blog", path: "/blog" },
        { label: "Contact", path: "/contact" },
      ],
    },
    {
      title: "Resources",
      links: [
        { label: "Case Studies", path: "/case-studies" },
        { label: "Tools", path: "/tools" },
        { label: "Support", path: "/support" },
        { label: "FAQ", path: "/faq" },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#0b1220] text-white flex flex-col">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-[#0b1220]/95 backdrop-blur-lg shadow-2xl border-b border-white/10"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center space-x-3 group"
              onClick={() => trackEvent("Navigation", "Logo", "Header")}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-[#E5C558] blur-xl opacity-50 group-hover:opacity-75 transition-opacity" />
                <div className="relative w-12 h-12 bg-gradient-to-br from-[#E5C558] to-[#d4b54d] rounded-xl flex items-center justify-center font-bold text-[#0b1220] text-xl">
                  GN
                </div>
              </div>
              <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">
                GeniusNexa
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => trackEvent("Navigation", link.label, "Header")}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                    location.pathname === link.path
                      ? "bg-[#E5C558] text-[#0b1220]"
                      : "text-gray-300 hover:text-white hover:bg-white/10"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <Link
                to="/contact"
                onClick={() => trackEvent("CTA", "Header_GetStarted", "Click")}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-[#0b1220] rounded-full font-semibold hover:shadow-xl hover:shadow-[#E5C558]/50 transition-all duration-300 hover:scale-105"
              >
                Get Started
                <svg
                  className="w-5 h-5 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
              aria-label="Toggle menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {mobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-white/10 bg-[#0b1220]/98 backdrop-blur-lg">
            <div className="px-4 py-6 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                    location.pathname === link.path
                      ? "bg-[#E5C558] text-[#0b1220]"
                      : "text-gray-300 hover:bg-white/10"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                className="block px-4 py-3 mt-4 bg-[#E5C558] text-[#0b1220] rounded-lg font-semibold text-center"
              >
                Get Started
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="flex-grow pt-20">{children}</main>

      {/* Footer */}
      <footer className="relative bg-gradient-to-b from-[#0b1220] to-[#050a14] border-t border-white/10 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
            {/* Brand */}
            <div className="lg:col-span-2">
              <Link to="/" className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-[#E5C558] to-[#d4b54d] rounded-lg flex items-center justify-center font-bold text-[#0b1220]">
                  GN
                </div>
                <span className="text-xl font-bold">GeniusNexa</span>
              </Link>
              <p className="text-gray-400 mb-6 max-w-sm">
                Transforming businesses through innovative technology solutions
                and digital excellence.
              </p>
              <div className="flex space-x-4">
                {["linkedin", "twitter", "github", "facebook"].map((social) => (
                  <a
                    key={social}
                    href={`#${social}`}
                    onClick={() => trackEvent("Social", social, "Footer")}
                    className="w-10 h-10 rounded-lg bg-white/5 hover:bg-[#E5C558]/20 flex items-center justify-center transition-colors"
                    aria-label={social}
                  >
                    <span className="text-gray-400">📱</span>
                  </a>
                ))}
              </div>
            </div>

            {/* Footer Links */}
            {footerSections.map((section) => (
              <div key={section.title}>
                <h3 className="font-semibold text-white mb-4">
                  {section.title}
                </h3>
                <ul className="space-y-2">
                  {section.links.map((link) => (
                    <li key={link.path}>
                      <Link
                        to={link.path}
                        onClick={() =>
                          trackEvent("Footer", link.label, "Click")
                        }
                        className="text-gray-400 hover:text-[#E5C558] transition-colors text-sm"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} GeniusNexa. All rights reserved.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <Link
                to="/privacy"
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                Terms of Service
              </Link>
              <Link
                to="/sitemap"
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                Sitemap
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Contact Button */}
      <a
        href="#contact"
        onClick={() => trackEvent("CTA", "FloatingContact", "Click")}
        className="fixed bottom-8 right-8 z-40 bg-[#E5C558] hover:bg-[#d4b54d] text-[#0b1220] rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110"
        aria-label="Contact Us"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
          />
        </svg>
      </a>
    </div>
  );
};

export default Layout;
